import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabase'

export const useAuth = () => {
  const [user, setUser] = useState(null)
  const [userRole, setUserRole] = useState('student')
  const [dbUser, setDbUser] = useState(null)
  const [dbUserError, setDbUserError] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isProvisioning, setIsProvisioning] = useState(false)
  const hasEnsuredRef = useRef(false)
  const isProvisioningRef = useRef(false)

  const createUserIfNotExists = useCallback(async (userObj) => {
    try {
      const email = userObj?.email
      const googleId = userObj?.id
      const fullName = userObj?.user_metadata?.full_name || userObj?.user_metadata?.name
      
      // Derive name from email if no full_name available
      const name = fullName || (email ? email.split('@')[0].replace(/[._]/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'User')
      
      console.log('[useAuth] createUserIfNotExists: creating user', { email, googleId, name })
      
      const { data, error } = await supabase
        .from('User')
        .insert({
          email: email,
          google_id: String(googleId),
          name: name,
          role: 'member',
          is_admin: false,
          should_email: true,
          session_type: 'main'
        })
        .select('id, email, role, is_admin, should_email, session_type, google_id, name')
        .single()

      if (error) {
        // If error is due to unique constraint violation, fetch existing user directly
        if (error.code === '23505' || error.message?.includes('duplicate') || error.message?.includes('unique')) {
          console.log('[useAuth] createUserIfNotExists: user already exists, fetching directly...')
          // Fetch directly without recursion
          const authId = userObj?.id
          const userEmail = userObj?.email
          
          let { data: existingData } = await supabase
            .from('User')
            .select('id, email, role, is_admin, should_email, session_type, google_id, name')
            .eq('google_id', String(authId))
            .maybeSingle()
          
          if (!existingData && userEmail) {
            const emailResult = await supabase
              .from('User')
              .select('id, email, role, is_admin, should_email, session_type, google_id, name')
              .eq('email', userEmail)
              .maybeSingle()
            existingData = emailResult.data
          }
          
          if (existingData) {
            console.log('[useAuth] createUserIfNotExists: found existing user', existingData)
            return existingData
          }
        }
        throw error
      }

      console.log('[useAuth] createUserIfNotExists: user created successfully', data)
      return data
    } catch (error) {
      console.error('[useAuth] createUserIfNotExists: error creating user:', error)
      throw error
    }
  }, [])

  const ensureUserViaServer = useCallback(async (userObj) => {
    // Prevent infinite loops - only run once per session
    if (hasEnsuredRef.current || isProvisioningRef.current) {
      console.log('[useAuth] ensureUserViaServer: already ensured or provisioning, skipping')
      return null
    }

    try {
      isProvisioningRef.current = true
      setIsProvisioning(true)
      hasEnsuredRef.current = true

      // Get session token
      const { data: { session } } = await supabase.auth.getSession()
      const token = session?.access_token

      if (!token) {
        console.warn('[useAuth] ensureUserViaServer: no access token available')
        return null
      }

      console.log('[useAuth] ensureUserViaServer: calling server endpoint')
      const response = await fetch('/api/auth/ensure-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })

      const result = await response.json()

      if (!response.ok) {
        console.error('[useAuth] ensureUserViaServer: server error', result.error)
        return null
      }

      console.log('[useAuth] ensureUserViaServer: user ensured', result.user)
      return result.user
    } catch (error) {
      console.error('[useAuth] ensureUserViaServer: exception', error)
      return null
    } finally {
      isProvisioningRef.current = false
      setIsProvisioning(false)
    }
  }, [])

  const fetchUserRole = useCallback(async (userObj) => {
    console.log('[useAuth] fetchUserRole: starting for user', userObj.id, userObj.email)
    try {
      const authId = userObj?.id
      const email = userObj?.email
      
      if (authId) {
        // Query User table by google_id first (preferred)
        let { data, error } = await supabase
          .from('User')
          .select('id, email, role, is_admin, should_email, session_type, google_id, name')
          .eq('google_id', String(authId))
          .maybeSingle()

        console.log('dbUser lookup by google_id', { authId, data, error })

        // Fallback: try email if google_id lookup fails
        if (!data && email && (!error || error.code === 'PGRST116')) {
          console.log('[useAuth] fetchUserRole: trying email fallback', email)
          const emailResult = await supabase
            .from('User')
            .select('id, email, role, is_admin, should_email, session_type, google_id, name')
            .eq('email', email)
            .maybeSingle()
          
          data = emailResult.data
          error = emailResult.error
          console.log('dbUser lookup by email', { email, data, error })
        }

        if (!error && data) {
          setDbUserError(null)
          setDbUser(data)
          const role = (data.role || '').toLowerCase()
          setUserRole(role || (data.is_admin ? 'admin' : 'member'))
          console.log('[useAuth] fetchUserRole: setting role to', role || (data.is_admin ? 'admin' : 'member'), 'from dbUser:', data)
          // Reset ensure flag when user is found
          hasEnsuredRef.current = false
          return data
        } else {
          // User not found - try server-side provisioning first
          console.log('[useAuth] fetchUserRole: no user found, trying server-side provisioning...')
          try {
            const serverUser = await ensureUserViaServer(userObj)
            if (serverUser) {
              // After server provisioning succeeds, refetch dbUser to ensure consistency
              const { data: refetchedData, error: refetchError } = await supabase
                .from('User')
                .select('id, email, role, is_admin, should_email, session_type, google_id, name')
                .eq('google_id', String(authId))
                .maybeSingle()
              
              if (!refetchError && refetchedData) {
                setDbUserError(null)
                setDbUser(refetchedData)
                const role = (refetchedData.role || '').toLowerCase()
                setUserRole(role || (refetchedData.is_admin ? 'admin' : 'member'))
                console.log('[useAuth] fetchUserRole: server-provisioned user, refetched and set role to', role || (refetchedData.is_admin ? 'admin' : 'member'))
                return refetchedData
              } else {
                // Use server response if refetch fails
                setDbUserError(null)
                setDbUser(serverUser)
                const role = (serverUser.role || '').toLowerCase()
                setUserRole(role || (serverUser.is_admin ? 'admin' : 'member'))
                console.log('[useAuth] fetchUserRole: server-provisioned user (using server response), setting role to', role || (serverUser.is_admin ? 'admin' : 'member'))
                return serverUser
              }
            }
          } catch (provisionError) {
            console.error('[useAuth] fetchUserRole: error server-provisioning user:', provisionError)
            setDbUserError(provisionError)
          }

          // Fallback to client-side creation if server provisioning fails
          console.log('[useAuth] fetchUserRole: falling back to client-side creation...')
          try {
            const newUser = await createUserIfNotExists(userObj)
            if (newUser) {
              setDbUserError(null)
              setDbUser(newUser)
              const role = (newUser.role || '').toLowerCase()
              setUserRole(role || (newUser.is_admin ? 'admin' : 'member'))
              console.log('[useAuth] fetchUserRole: auto-created user, setting role to', role || (newUser.is_admin ? 'admin' : 'member'))
              return newUser
            }
          } catch (createError) {
            console.error('[useAuth] fetchUserRole: error auto-creating user:', createError)
            setDbUserError(createError)
            setDbUser(null)
            setUserRole('member')
            return null
          }
          
          setDbUser(null)
          setUserRole('member')
          setDbUserError(error || null)
          if (error) {
            console.error('[useAuth] fetchUserRole: error fetching user from database:', error)
          }
          return null
        }
      } else {
        console.warn('[useAuth] fetchUserRole: no authId available for user:', userObj)
        setDbUser(null)
        setUserRole('member')
        setDbUserError(null)
        return null
      }
    } catch (error) {
      console.error('[useAuth] fetchUserRole: exception in fetchUserRole:', error)
      setDbUserError(error)
      setDbUser(null)
      setUserRole('member') // Fallback role
      return null
    }
    // Note: Do NOT setLoading(false) here - that's handled by the caller
  }, [createUserIfNotExists, ensureUserViaServer])

  useEffect(() => {
    let mounted = true
    let subscription = null

    console.log('[useAuth] useEffect: initialization starting')

    // Initialize auth state: get session first, then set up listener
    const initializeAuth = async () => {
      try {
        // Check if there's an OAuth callback hash in the URL
        const hash = window.location.hash
        const hasOAuthHash = hash && (
          hash.includes('access_token') ||
          hash.includes('refresh_token') ||
          hash.includes('error')
        )

        if (hasOAuthHash) {
          console.log('[useAuth] Detected OAuth callback hash on initialization')
        }

        // Get initial session from storage (or from hash if present)
        // Supabase automatically processes hash tokens when getSession() is called
        const { data: { session }, error: sessionError } = await supabase.auth.getSession()
        
        console.log('[useAuth] getSession on mount:', { 
          hasSession: !!session, 
          userId: session?.user?.id,
          userEmail: session?.user?.email,
          error: sessionError 
        })
        
        if (!mounted) {
          console.log('[useAuth] getSession: component unmounted, skipping')
          return
        }
        
        if (sessionError) {
          console.error('[useAuth] getSession error:', sessionError)
          setLoading(false)
          return
        }
        
        // If we processed a hash, clear it from the URL now that session is set
        if (hasOAuthHash) {
          console.log('[useAuth] Clearing OAuth callback hash from URL')
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname + window.location.search
          )
        }
        
        // Set user from session (don't set to null while loading)
        const userFromSession = session?.user ?? null
        console.log('[useAuth] Setting user from initial session:', userFromSession?.email || 'null')
        setUser(userFromSession)
        
        if (session?.user) {
          // Fetch role asynchronously, but don't block on it
          fetchUserRole(session.user)
            .then(() => {
              console.log('[useAuth] Initial role fetched successfully')
            })
            .catch(err => {
              console.error('[useAuth] getSession: error in fetchUserRole:', err)
            })
            .finally(() => {
              if (mounted) {
                setLoading(false)
              }
            })
        } else {
          console.log('[useAuth] getSession: no session found, setting loading to false')
          setLoading(false)
        }
      } catch (error) {
        console.error('[useAuth] initializeAuth exception:', error)
        if (mounted) {
          setLoading(false)
        }
      }

      // Set up auth state change listener AFTER initial session is loaded
      // This prevents race conditions and ensures we have the correct initial state
      if (mounted) {
        const { data } = supabase.auth.onAuthStateChange(
          async (event, session) => {
            console.log('[useAuth] onAuthStateChange:', {
              event,
              hasSession: !!session,
              userEmail: session?.user?.email,
              userId: session?.user?.id
            })
            
            if (!mounted) {
              console.log('[useAuth] onAuthStateChange: component unmounted, skipping')
              return
            }
            
            try {
              // Update user state from session - ensure this happens reliably
              const userFromSession = session?.user ?? null
              console.log('[useAuth] Setting user from session:', userFromSession?.email || 'null')
              
              // Always update user state, even if null (for sign out)
              setUser(userFromSession)
              
              if (session?.user) {
                // Fetch role asynchronously - don't block on this
                fetchUserRole(session.user)
                  .then(() => {
                    console.log('[useAuth] onAuthStateChange: role fetched successfully')
                  })
                  .catch(err => {
                    console.error('[useAuth] onAuthStateChange: error in fetchUserRole:', err)
                  })
              } else {
                // No session - reset to default state
                console.log('[useAuth] onAuthStateChange: no session, resetting to default state')
                setUserRole('student')
                setDbUser(null)
                setDbUserError(null)
                hasEnsuredRef.current = false // Reset ensure flag on sign out
              }
              
              // Ensure loading is false after auth state change
              if (mounted) {
                setLoading(false)
              }
            } catch (error) {
              console.error('[useAuth] onAuthStateChange: exception in handler:', error)
              if (mounted) {
                setLoading(false)
              }
            }
          }
        )
        subscription = data.subscription
      }
    }

    initializeAuth()

    return () => {
      console.log('[useAuth] useEffect: cleanup - unmounting')
      mounted = false
      if (subscription) {
        subscription.unsubscribe()
      }
    }
  }, [fetchUserRole])

  const signInWithGoogle = async () => {
    // Save current location to return to after sign-in
    const returnTo = window.location.pathname + window.location.search + window.location.hash
    sessionStorage.setItem('returnTo', returnTo)
    
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin
      }
    })
    return { data, error }
  }

  const signOut = async () => {
    console.log('[useAuth] signOut: starting')
    const { error } = await supabase.auth.signOut()
    
    if (error) {
      console.error('[useAuth] signOut error:', error)
      return { error }
    }
    
    // Clear all auth state after successful sign out
    console.log('[useAuth] signOut: clearing state')
    setUser(null)
    setUserRole('student')
    setDbUser(null)
    setDbUserError(null)
    
    return { error: null }
  }

  return {
    user,
    userRole,
    dbUser,
    dbUserError,
    loading,
    isProvisioning,
    signInWithGoogle,
    signOut
  }
}
